import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux';
import { useModalManager } from '../../../hooks/useModalManager';
import WelcomeModal from '../../../components/modal/WelcomeModal';
import ModalRenderer from '../../../components/modal/ModalRenderer';
import DashboardTable from './DashboardTable';
import DspDashboard from '../../../pages/dsp/DspDashboard';
import { usePitchesStorage } from '../../../hooks/usePitchesStorage';
import { userApi } from '../../../repositories/user';
import { setUser } from '../../../store/slices/userSlice';

// Constants
const draftReminderDismissedKey = 'draft_reminder_dismissed_';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const userProfile = useAppSelector((state) => state.user);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const { pitches } = usePitchesStorage();
  const { isOpen, openModal, closeModal } = useModalManager();



  // Backend already filters and sends only urgent draft pitches (within 2 weeks)
  const urgentPitches = pitches || [];

  useEffect(() => {
    if (!user) return;

    if (user.role !== 'basic') return;

    if (showWelcomeModal) return;

    // Check if user has dismissed the reminder from the backend
    if (userProfile.hasDismissedDraftPitchReminder) return;

    const reminderDismissed = sessionStorage.getItem(`${draftReminderDismissedKey}${user.id}`);
    if (reminderDismissed) return;

    if (urgentPitches.length > 0) {
      openModal('DRAFT_PITCH_REMINDER');
    }
  }, [user, userProfile.hasDismissedDraftPitchReminder, showWelcomeModal, urgentPitches.length, openModal]);

  const handleWelcomeClose = useCallback(() => {
    if (user) {
      localStorage.setItem(`welcome_shown_${user.id}`, 'true');
    }
    setShowWelcomeModal(false);
  }, [user]);

  const handleGoToSettings = useCallback(() => {
    if (user) {
      localStorage.setItem(`welcome_shown_${user.id}`, 'true');
    }
    setShowWelcomeModal(false);
    navigate('/settings?tab=dsps');
  }, [user, navigate]);

  const handleViewDrafts = useCallback(() => {
    closeModal('DRAFT_PITCH_REMINDER');

    if (urgentPitches.length > 0) {
      const firstDraftId = urgentPitches[0].id;
      navigate(`/pitch/edit/${firstDraftId}?from=dashboard`);
    } else {
      navigate('/pitch/create');
    }
  }, [urgentPitches, navigate, closeModal]);

  const handleRemindLater = useCallback(() => {
    if (user) {
      // Store in sessionStorage only - will show again on next login
      sessionStorage.setItem(`${draftReminderDismissedKey}${user.id}`, 'true');
    }
    closeModal('DRAFT_PITCH_REMINDER');
  }, [user, closeModal]);

  const handleDismissReminder = useCallback(async () => {
    if (user) {
      try {
        // Call API to permanently dismiss the reminder in the database
        await userApi.dismissDraftPitchReminder(user.id);

        // Update Redux store to reflect the dismissal
        dispatch(setUser({ hasDismissedDraftPitchReminder: true }));

        closeModal('DRAFT_PITCH_REMINDER');
      } catch (error) {
        console.error('Failed to dismiss draft pitch reminder:', error);
        closeModal('DRAFT_PITCH_REMINDER');
      }
    }
  }, [user, dispatch, closeModal]);

  // If user is DSP, show DSP dashboard
  if (user?.role === 'dsp') {
    return <DspDashboard />;
  }

  return (
    <div className="dashboard-container py-2 sm:p-6">
      <DashboardTable />

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeClose}
        onGoToSettings={handleGoToSettings}
      />

      {/* Draft Pitch Reminder Modal - Only for basic role users */}
      {user?.role === 'basic' && (
        <ModalRenderer
          type="DRAFT_PITCH_REMINDER"
          isOpen={isOpen('DRAFT_PITCH_REMINDER')}
          userName={user?.email?.split('@')[0] || 'User'}
          draftPitches={urgentPitches}
          onViewDrafts={handleViewDrafts}
          onRemindLater={handleRemindLater}
          onDismiss={handleDismissReminder}
        />
      )}
    </div>
  )
}

export default Dashboard;